import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/api/client";

/**
 * Debounced public search — uses Algolia via backend when configured, Mongo fallback otherwise.
 */
export function usePublicSearch({ slug, lang, query, limit = 10, enabled = true }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState("mongo");
  const ctrlRef = useRef(null);

  const langParam = lang === "all" ? "all" : (lang || "en");

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    if (ctrlRef.current) ctrlRef.current.abort();
    ctrlRef.current = new AbortController();
    setLoading(true);
    try {
      const { data } = await api.get(
        `/public/search?space=${encodeURIComponent(slug)}&q=${encodeURIComponent(q.trim())}&lang=${langParam}`,
        { signal: ctrlRef.current.signal },
      );
      setResults((data.results || []).slice(0, limit));
      setProvider(data.provider || "mongo");
    } catch (e) {
      if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  }, [slug, langParam, limit]);

  useEffect(() => {
    if (!enabled) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => search(query), 200);
    return () => clearTimeout(t);
  }, [query, enabled, search]);

  return { results, loading, provider };
}

export function useAdminSearch({ query, lang, enabled = true }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const ctrlRef = useRef(null);

  useEffect(() => {
    if (!enabled || !query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      if (ctrlRef.current) ctrlRef.current.abort();
      ctrlRef.current = new AbortController();
      setLoading(true);
      try {
        const params = new URLSearchParams({ q: query.trim() });
        if (lang) params.set("lang", lang);
        const { data } = await api.get(`/search/admin?${params}`, { signal: ctrlRef.current.signal });
        setResults(data.results || []);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, lang, enabled]);

  return { results, loading };
}
